import { SignClient } from '@walletconnect/sign-client';
import { ethers } from 'ethers';

export const WC_PROJECT_ID = 'c7ce496ee46c6b7e53ecf2eaed521b1e';

// ── Архитектура (переписано 31.08 после стенда) ─────────────────────────────
//
// ОДИН SignClient на всё приложение, у него НЕСКОЛЬКО сессий — по одной на
// кошелёк (Rainbow, Rabby). Так работает сам протокол: сессии адресуются
// топиками, один клиент спокойно ведёт их параллельно.
//
// Прошлая схема «EthereumProvider на кошелёк» создавала несколько ядер
// WalletConnect в одном вебвью — библиотека прямо предупреждает, что это
// ломает поведение (проверено на стенде: «Core is already initialized…»,
// «No listener for session_proposal», запросы уходят не в тот экземпляр).
// Отсюда были все «подключал Rainbow — считается Rabby» и пропавшие
// подтверждения.
//
// Каждому кошельку наружу отдаётся лёгкий EIP-1193-шим:
//  - подписи (eth_sendTransaction и т.п.) → signClient.request по топику сессии;
//  - чтения (estimateGas, call, receipts…) → публичный RPC Arbitrum напрямую;
//  - eth_chainId/eth_accounts — локально (сеть у нас одна, прибита).
// ethers.BrowserProvider(шим) работает как раньше.

export const isTelegramWebview = () =>
    typeof window !== 'undefined' && Boolean(window.Telegram?.WebApp?.initData);

// universal — https-ссылка (открываем через Telegram.openLink);
// у Rabby универсальной нет (по реестру WalletConnect) — только нативная
// схема через https-трамплин public/deeplink.html.
export const WC_WALLETS = [
    { key: 'rainbow',  name: 'Rainbow',  universal: 'https://rnbwapp.com' },
    // Временно только Rainbow: две одновременные сессии глючат.
    // Вернуть Rabby: { key: 'rabby', name: 'Rabby', native: 'rabby://', androidPackage: 'com.debank.rabbymobile' },
    // Вернуть MetaMask: { key: 'metamask', name: 'MetaMask', universal: 'https://metamask.app.link' }
];

const walletByKey = (key) => WC_WALLETS.find((w) => w.key === key);

// Активный кошелёк: его сессия подписывает транзакции, в него прыгаем.
const LAST_WALLET_KEY = 'wc_last_wallet';
export const setLastWallet = (key) => { try { localStorage.setItem(LAST_WALLET_KEY, key); } catch {} };
export const getLastWallet = () => {
    try {
        const k = localStorage.getItem(LAST_WALLET_KEY);
        return walletByKey(k) ? k : WC_WALLETS[0].key;
    } catch { return WC_WALLETS[0].key; }
};

const PINNED_CHAIN = 42161;
const CAIP_CHAIN = `eip155:${PINNED_CHAIN}`;
const RPC_URL = 'https://arb1.arbitrum.io/rpc';

const REQUIRED_NAMESPACES = {
    eip155: {
        methods: [
            'eth_sendTransaction', 'eth_signTransaction', 'personal_sign',
            'eth_sign', 'eth_signTypedData', 'eth_signTypedData_v3', 'eth_signTypedData_v4',
        ],
        chains: [CAIP_CHAIN],
        events: ['chainChanged', 'accountsChanged'],
    },
};

// ── Топики сессий по кошелькам ──────────────────────────────────────────────
const TOPICS_KEY = 'wc_wallet_topics';
const topicsMap = () => {
    try { return JSON.parse(localStorage.getItem(TOPICS_KEY) || '{}'); } catch { return {}; }
};
const setTopic = (key, topic) => {
    try {
        const t = topicsMap();
        if (topic) t[key] = topic; else delete t[key];
        localStorage.setItem(TOPICS_KEY, JSON.stringify(t));
    } catch {}
};
const dropTopicEverywhere = (topic) => {
    const t = topicsMap();
    for (const k of Object.keys(t)) if (t[k] === topic) setTopic(k, null);
};

// ── Диплинки ────────────────────────────────────────────────────────────────
// Вебвью Telegram режет кастомные схемы и теряет uri — поэтому:
// universal link через Telegram.openLink, нативные схемы через трамплин.
// uri кодируем ровно один раз (двойное кодирование ломает диплинк).
const openWalletLink = (wallet, path) => {
    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    if (wallet.universal) {
        const link = `${wallet.universal}/${path}`;
        if (tg?.openLink) tg.openLink(link);
        else window.open(link, '_blank', 'noopener');
    } else {
        const scheme = wallet.native.replace('://', '');
        const jump = `${window.location.origin}/deeplink.html?scheme=${scheme}` +
            (wallet.androidPackage ? `&package=${wallet.androidPackage}` : '') +
            (path.includes('uri=') ? `&${path.slice(path.indexOf('uri='))}` : '');
        if (tg?.openLink) tg.openLink(jump);
        else window.open(jump, '_blank', 'noopener');
    }
};

export function openWalletApp(uri, key) {
    const wallet = walletByKey(key) || walletByKey(getLastWallet()) || WC_WALLETS[0];
    openWalletLink(wallet, `wc?uri=${encodeURIComponent(uri)}`);
}

// Подбросить юзера в приложение АКТИВНОГО кошелька — там ждёт запрос подписи.
export function bringWalletToFront() {
    if (!isTelegramWebview()) return;
    const wallet = walletByKey(getLastWallet()) || WC_WALLETS[0];
    openWalletLink(wallet, 'wc');
}

// ── SignClient (единственный) ───────────────────────────────────────────────
let _client = null;
let _clientPromise = null;

const isStaleSessionError = (err) => {
    const msg = String(err?.message || err || '');
    return (
        msg.includes('No matching key') ||
        msg.includes("session topic doesn't exist") ||
        msg.includes('session: Topic not found') ||
        msg.includes('expired')
    );
};

// Полная зачистка локального состояния WC (вместе с легаси старых схем)
const purgeAllWCStorage = () => {
    if (typeof localStorage === 'undefined') return;
    Object.keys(localStorage).forEach((k) => {
        const lower = k.toLowerCase();
        if (
            k === TOPICS_KEY ||
            k.startsWith('wc_kv:') ||       // легаси прошлых архитектур
            k.startsWith('wc@') ||
            k.startsWith('wc:') ||
            lower.startsWith('walletconnect') ||
            lower.includes('wcm_')
        ) {
            try { localStorage.removeItem(k); } catch {}
        }
    });
};

// Одноразовая уборка легаси-неймспейсов старой мультипровайдерной схемы
const cleanupLegacy = () => {
    try {
        Object.keys(localStorage).forEach((k) => {
            if (k.startsWith('wc_kv:')) localStorage.removeItem(k);
        });
    } catch {}
};

// Поздние ответы релея (вебвью спал, пока юзер подтверждал в кошельке) приводят
// к throw внутри sign-client: «emitting session_ping:<id> without any listeners» —
// он валится глобальной ошибкой и в dev застилает экран красным оверлеем.
// Слушателя уже нет — ответ никому не нужен, это шум. Патчим listenerCount движка:
// для событий-ответов (имя:id) отвечаем «слушатель есть», и emit уходит в пустоту
// вместо throw. Живые ожидания не затрагиваются (там счётчик и так ≥ 1).
const muteLateRelayResponses = (client) => {
    try {
        const ev = client?.engine?.events;
        if (!ev || ev.__latePatch) return;
        const orig = ev.listenerCount.bind(ev);
        ev.listenerCount = (name, ...rest) => {
            const n = orig(name, ...rest);
            return String(name).includes(':') ? Math.max(n, 1) : n;
        };
        ev.__latePatch = true;
    } catch {}
};

async function initClient() {
    if (_client) return _client;
    if (_clientPromise) return _clientPromise;
    _clientPromise = (async () => {
        try {
            cleanupLegacy();
            const client = await SignClient.init({
                projectId: WC_PROJECT_ID,
                metadata: {
                    name: 'OctoPlatform',
                    description: 'Crypto giveaway platform',
                    url: 'https://api.duotetra.quest',
                    icons: ['https://api.duotetra.quest/favicon.ico'],
                },
            });
            client.on('session_delete', ({ topic }) => dropTopicEverywhere(topic));
            client.on('session_expire', ({ topic }) => dropTopicEverywhere(topic));
            client.on('session_event', (ev) => {
                if (ev?.params?.event?.name === 'accountsChanged') {
                    try { window.dispatchEvent(new CustomEvent('wc:sessions-changed')); } catch {}
                }
            });
            // Продлеваем живые сессии — реже протухают у кошельков
            for (const topic of Object.values(topicsMap())) {
                try { client.extend({ topic }).catch(() => {}); } catch {}
            }
            muteLateRelayResponses(client);
            _client = client;
            return client;
        } catch (err) {
            if (isStaleSessionError(err)) {
                purgeAllWCStorage();
                const client = await SignClient.init({
                    projectId: WC_PROJECT_ID,
                    metadata: {
                        name: 'OctoPlatform',
                        description: 'Crypto giveaway platform',
                        url: 'https://api.duotetra.quest',
                        icons: ['https://api.duotetra.quest/favicon.ico'],
                    },
                });
                muteLateRelayResponses(client);
                _client = client;
                return client;
            }
            throw err;
        } finally {
            _clientPromise = null;
        }
    })();
    return _clientPromise;
}

// Живая (не просроченная) сессия кошелька из клиента; null если нет
const getSessionRecord = (key) => {
    const topic = topicsMap()[key];
    if (!topic || !_client) return null;
    try {
        const s = _client.session.get(topic);
        if (!s) return null;
        if (Number(s.expiry || 0) * 1000 < Date.now()) return null;
        return s;
    } catch { return null; }
};

const sessionAddress = (s) => {
    const acc = s?.namespaces?.eip155?.accounts?.[0];
    return acc ? acc.split(':').pop() : null;
};

// Сессия кошелька для UI: { address, expired } | null.
// Сначала спрашиваем живой клиент; если он ещё не поднят — читаем его стор
// прямо из localStorage (браузерный SignClient персистит туда).
export function getStoredSession(key) {
    const topic = topicsMap()[key];
    if (!topic) return null;
    if (_client) {
        try {
            const s = _client.session.get(topic);
            if (s) {
                return {
                    address: sessionAddress(s),
                    expired: Number(s.expiry || 0) * 1000 < Date.now(),
                };
            }
            return null; // клиент жив, но сессии нет — она удалена
        } catch { return null; }
    }
    try {
        for (const k of Object.keys(localStorage)) {
            if (!k.startsWith('wc@') || !k.endsWith('//session')) continue;
            const arr = JSON.parse(localStorage.getItem(k) || '[]');
            const s = (Array.isArray(arr) ? arr : []).find((x) => x?.topic === topic);
            if (s) {
                return {
                    address: sessionAddress(s),
                    expired: Number(s.expiry || 0) * 1000 < Date.now(),
                };
            }
        }
    } catch {}
    return null;
}

// Сессии всех кошельков: { key: адрес }. Заодно прогреваем клиент в фоне.
export async function getWalletSessions() {
    initClient().catch(() => {});
    const map = {};
    for (const w of WC_WALLETS) {
        const s = getStoredSession(w.key);
        if (s?.address) map[w.key] = s.address;
    }
    return map;
}

// ── EIP-1193-шим на кошелёк ─────────────────────────────────────────────────
const SIGN_METHODS = new Set([
    'eth_sendTransaction', 'eth_signTransaction', 'personal_sign', 'eth_sign',
    'eth_signTypedData', 'eth_signTypedData_v3', 'eth_signTypedData_v4',
]);

let _rpc = null;
const rpc = () => {
    if (!_rpc) _rpc = new ethers.JsonRpcProvider(RPC_URL, PINNED_CHAIN, { staticNetwork: true });
    return _rpc;
};

const _shims = {};

function makeShim(key) {
    const shim = {
        walletKey: key,
        chainId: PINNED_CHAIN,
        get signClient() { return _client; },
        get session() { return getSessionRecord(key) || undefined; },
        get connected() { return Boolean(getSessionRecord(key)); },
        get accounts() {
            const a = sessionAddress(getSessionRecord(key));
            return a ? [a] : [];
        },
        on() {}, off() {}, once() {}, removeListener() {},

        // Полный коннект: старую сессию этого кошелька закрываем, открываем
        // новую пару, сами открываем нужное приложение диплинком.
        async connect() {
            const client = await initClient();
            const old = topicsMap()[key];
            if (old) {
                try { await client.disconnect({ topic: old, reason: { code: 6000, message: 'reconnect' } }); } catch {}
                setTopic(key, null);
            }
            const { uri, approval } = await client.connect({ requiredNamespaces: REQUIRED_NAMESPACES });
            if (uri) openWalletApp(uri, key);
            const session = await approval();
            setTopic(key, session.topic);
            return session;
        },

        async disconnect() {
            await purgeWCSession(key);
        },

        async request(args) {
            const { method, params } = args || {};
            if (method === 'eth_accounts' || method === 'eth_requestAccounts') return this.accounts;
            if (method === 'eth_chainId') return '0x' + PINNED_CHAIN.toString(16);
            if (method === 'net_version') return String(PINNED_CHAIN);
            // Сеть одна и прибита — переключать нечего, отвечаем успехом сразу
            if (method === 'wallet_switchEthereumChain' || method === 'wallet_addEthereumChain') return null;

            if (SIGN_METHODS.has(method)) {
                const client = await initClient();
                const s = getSessionRecord(key);
                if (!s) throw new Error('Wallet session expired. Reconnect your wallet.');
                await ensureTransport(this);
                return client.request({
                    topic: s.topic,
                    chainId: CAIP_CHAIN,
                    request: { method, params },
                });
            }

            // Всё остальное — чтения, шлём в публичный RPC напрямую
            return rpc().send(method, params || []);
        },
    };
    return shim;
}

// Провайдер кошелька. Без аргумента — активного.
export async function getWCProvider(walletKey) {
    const key = walletByKey(walletKey) ? walletKey : getLastWallet();
    await initClient();
    if (!_shims[key]) _shims[key] = makeShim(key);
    return _shims[key];
}

// ── Здоровье и обслуживание ─────────────────────────────────────────────────

export async function ensureTransport(providerLike) {
    try {
        const relayer = (providerLike?.signClient || _client)?.core?.relayer;
        if (relayer && !relayer.connected) {
            await relayer.restartTransport();
        }
    } catch {}
}

// Пинг сессии кошелька через relay.
//   true — кошелёк ответил; false — сессия мертва; null — не ясно (спит).
export async function pingWCSession(walletKey, timeoutMs = 4000) {
    const key = walletByKey(walletKey) ? walletKey : getLastWallet();
    try {
        const client = await initClient();
        const s = getSessionRecord(key);
        if (!s) return false;
        await ensureTransport({ signClient: client });
        return await Promise.race([
            client.ping({ topic: s.topic }).then(() => true),
            new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
        ]);
    } catch (e) {
        return isStaleSessionError(e) ? false : null;
    }
}

// Вебвью Telegram рвёт сокет к relay при уходе в кошелёк —
// при возврате переподнимаем.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || !_client) return;
        try {
            const p = _client.core?.relayer?.restartTransport?.();
            if (p?.catch) p.catch(() => {});
        } catch {}
    });
}

// connect() с таймаутом: вместо вечного спиннера — ошибка WC_CONNECT_TIMEOUT.
export const WC_CONNECT_TIMEOUT = 'WC_CONNECT_TIMEOUT';

export async function connectWithTimeout(provider, ms = 90000) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(WC_CONNECT_TIMEOUT)), ms);
    });
    try {
        return await Promise.race([provider.connect(), timeout]);
    } finally {
        clearTimeout(timer);
    }
}

export function hasLiveWCSession() {
    return WC_WALLETS.some((w) => Boolean(getSessionRecord(w.key)));
}

export function resetWCProvider() {
    // Клиент один и общий; сбрасывать его не нужно — сессии живут отдельно.
}

// Снос сессии ОДНОГО кошелька: дисконнект + удаление топика.
// Остальные сессии не трогаем.
export async function purgeWCSession(walletKey) {
    const key = walletByKey(walletKey) ? walletKey : getLastWallet();
    const topic = topicsMap()[key];
    setTopic(key, null);
    if (topic && _client) {
        try { await _client.disconnect({ topic, reason: { code: 6000, message: 'user disconnected' } }); } catch {}
    }
}

export { isStaleSessionError };
